import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import {
  ANALYTICS_READ_SCOPE,
  DEMAND_NOTE,
  demandRow,
  freshnessNote,
  qualityRow,
  RANGES,
  RETENTION_NOTE,
  retentionRow,
  supplyRow,
  windowFor,
} from "../analytics.ts";
import type { BoardRow } from "../board.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * The four analytics boards, on DAT-04.
 *
 * **None of these four has a design file**, which is recorded as Q-186 and is the first thing to
 * know before reading them: every other board in this console is a reproduction of
 * `17 WP16 Tenant Admin.dc.html` and can be held against it, and these cannot. They are built at
 * the client's direction, in the v2 token system, following the nearest designed board rather
 * than introducing a pattern.
 *
 * ## Four pages, one component file
 *
 * Each board is its own exported component, because each is its own page in `navigation.ts` with
 * its own columns, filter and export. They share a file because they share a shape — fetch a
 * series for a window, map its buckets to rows, draw a `RecordsBoard` — and splitting them into
 * four near-identical files would hide that the only differences are the columns and the mapper.
 *
 * ## What is deliberately not drawn
 *
 * **No chart.** No console on this platform draws one, so a chart here would be the first and
 * would have nothing to be faithful to. A table sorts, filters and exports to CSV, Excel and PDF,
 * which rule 4 requires of every grid and which is what an operator asking "how did last month
 * go" actually takes away.
 *
 * **No tile row.** R-04: a tile needs a total the listing publishes, and these publish buckets.
 *
 * **No comparison against a previous period.** It would be one more request and an obvious thing
 * to want, and it is not drawn because "down 4% on last month" is a claim about two windows and
 * this page would be inventing the second one. A reader changing the range sees both.
 */

const analytics = services.data_platform__tenant_analytics;

export interface AnalyticsBoardProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `CityCab › Analytics › Demand and revenue`. */
  readonly crumbs?: readonly string[];
  /** Injected in tests so a board's freshness line is not a moving target. */
  readonly now?: Date;
}

/** What every one of the four boards needs back from its own fetch. */
interface Loaded {
  readonly rows: readonly BoardRow[];
  readonly freshestFactAt: string | null;
}

/**
 * The shared body: a range picker, a fetch, and a `RecordsBoard`.
 *
 * The range lives here rather than in each board because it is the same control on all four, and
 * a reader moving between them should not find it behaving differently on one.
 */
/**
 * The clock a board reads, held still between renders.
 *
 * A defaulted `new Date()` parameter is a new object every render. Where that object reaches a
 * dependency array — and here it reaches one in `useSeries` — the effect behind it never
 * settles. A caller that passes a fixed `now`, which the tests do, gets exactly that value and
 * a stable identity for it; a caller that passes nothing gets one `Date` for the lifetime of
 * the board, which is what a board wants: the period it reports is the period it was opened
 * in, and it must not move under the reader.
 */
function useStableClock(given: Date | undefined): Date {
  return useMemo(() => given ?? new Date(), [given]);
}
function useSeries(
  session: Session,
  load: (window: { from: string; to: string }) => Promise<Loaded>,
  now: Date,
): {
  readonly range: string;
  readonly setRange: (value: string) => void;
  readonly loaded: Loaded | undefined;
  readonly failure: ViewState | undefined;
  readonly reload: () => void;
} {
  const [range, setRange] = useState<string>(RANGES[0].label);
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);

  const run = useCallback(async () => {
    setLoaded(undefined);
    setFailure(undefined);

    if (!session.scopes.has(ANALYTICS_READ_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [ANALYTICS_READ_SCOPE] });
      return;
    }

    try {
      setLoaded(await load(windowFor(range, now)));
    } catch (error) {
      setFailure(stateForError(error, [ANALYTICS_READ_SCOPE]));
    }
    /*
     * **The comment that stood here said `now` is deliberately not a dependency, and the array
     * below listed it.** The comment was right and the code was not.
     *
     * Each of the four boards takes `now = new Date()` as a defaulted parameter, which is
     * evaluated on every render, so the dependency changed on every render, so this callback
     * changed, so the effect calling it ran again, so it set state, so the component rendered.
     * Measured against the live console on 18 October: **eighty requests in eight seconds**,
     * each asking for a window a second wider than the last, because the clock moved between
     * renders. All four analytics boards did it, and the corporate portal had the same fault in
     * three more.
     *
     * `now` stays in the array and is stable instead. A dependency array that omits something
     * the callback closes over is a stale-closure bug waiting for the next edit; the honest fix
     * is a clock that does not change identity between renders, which is what each board now
     * passes in.
     */
  }, [range, session.scopes, load, now]);

  useEffect(() => {
    void run();
  }, [run]);

  return {
    range,
    setRange,
    loaded,
    failure,
    reload: (): void => {
      void run();
    },
  };
}

/** The range control, in the same shape the boards' other filters take. */
function rangeFilter(
  range: string,
  setRange: (value: string) => void,
): {
  unfiltered: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
} {
  return {
    // Not "All" — every one of these boards is always over some window, and an option meaning
    // "no window" would be an option the service refuses.
    unfiltered: RANGES[0].label,
    options: RANGES.map((entry) => ({ value: entry.label, label: entry.label })),
    value: range,
    onChange: setRange,
  };
}

export function DemandAnalytics({
  transport,
  session,
  crumbs,
  now: givenNow,
}: AnalyticsBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (window: { from: string; to: string }): Promise<Loaded> => {
      const series = await analytics
        .createTenantAnalyticsClient(transport)
        .getDemandAnalytics(session.tenantId, window.from, window.to, "day", undefined, undefined);
      return { rows: series.buckets.map(demandRow), freshestFactAt: series.freshestFactAt };
    },
    [transport, session.tenantId],
  );
  const { range, setRange, loaded, failure, reload } = useSeries(session, load, now);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Demand and revenue"
      source="DAT-04"
      description="What was completed and cancelled over time, and what it was worth."
      columns={["Period", "Completed", "Cancelled", "Cancellation rate", "Gross"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose="No completed or cancelled work was recorded for this tenant in the selected period."
      filter={rangeFilter(range, setRange)}
      note={{ kind: "info", glyph: "info", text: DEMAND_NOTE }}
      {...(loaded === undefined ? {} : { footnote: freshnessNote(loaded.freshestFactAt, now) })}
      exportName="analytics-demand"
      detailSubtitle="Demand and revenue · DAT-04"
      detailNote={DEMAND_NOTE}
    />
  );
}

export function SupplyAnalytics({
  transport,
  session,
  crumbs,
  now: givenNow,
}: AnalyticsBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (window: { from: string; to: string }): Promise<Loaded> => {
      const series = await analytics
        .createTenantAnalyticsClient(transport)
        .getSupplyAnalytics(session.tenantId, window.from, window.to, "day", undefined, undefined);
      return { rows: series.buckets.map(supplyRow), freshestFactAt: series.freshestFactAt };
    },
    [transport, session.tenantId],
  );
  const { range, setRange, loaded, failure, reload } = useSeries(session, load, now);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Supply and utilisation"
      source="DAT-04"
      description="How much driver time there was, and how much of it was working."
      columns={["Period", "Active drivers", "Online", "Engaged", "Utilisation", "Acceptance"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose="No driver shift or offer was recorded for this tenant in the selected period."
      filter={rangeFilter(range, setRange)}
      note={{
        kind: "info",
        glyph: "info",
        // Said because the two are easily read as the same number, and the gap between them is
        // the entire point of the board.
        text: "Utilisation is engaged time as a share of online time. A driver online and unoffered is supply this tenant is paying for in goodwill.",
      }}
      {...(loaded === undefined ? {} : { footnote: freshnessNote(loaded.freshestFactAt, now) })}
      exportName="analytics-supply"
      detailSubtitle="Supply and utilisation · DAT-04"
      detailNote="Online and engaged time are summed from driver shift segments."
    />
  );
}

export function RetentionAnalytics({
  transport,
  session,
  crumbs,
  now: givenNow,
}: AnalyticsBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (window: { from: string; to: string }): Promise<Loaded> => {
      const report = await analytics
        .createTenantAnalyticsClient(transport)
        .getRetentionAnalytics(session.tenantId, window.from, window.to);
      return { rows: report.cohorts.map(retentionRow), freshestFactAt: report.freshestFactAt };
    },
    [transport, session.tenantId],
  );
  const { range, setRange, loaded, failure, reload } = useSeries(session, load, now);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Customer retention"
      source="DAT-04"
      description="Customers grouped by the month they first appeared, and who came back."
      columns={["Cohort", "First seen", "Month 1", "Month 2", "Month 3"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose="No completed customer activity was recorded for this tenant in the selected period."
      filter={rangeFilter(range, setRange)}
      note={{ kind: "info", glyph: "info", text: RETENTION_NOTE }}
      {...(loaded === undefined ? {} : { footnote: freshnessNote(loaded.freshestFactAt, now) })}
      exportName="analytics-retention"
      detailSubtitle="Customer retention · DAT-04"
      detailNote={RETENTION_NOTE}
    />
  );
}

export function QualityAnalytics({
  transport,
  session,
  crumbs,
  now: givenNow,
}: AnalyticsBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (window: { from: string; to: string }): Promise<Loaded> => {
      const series = await analytics
        .createTenantAnalyticsClient(transport)
        .getQualityAnalytics(session.tenantId, window.from, window.to, "day", undefined, undefined);
      return { rows: series.buckets.map(qualityRow), freshestFactAt: series.freshestFactAt };
    },
    [transport, session.tenantId],
  );
  const { range, setRange, loaded, failure, reload } = useSeries(session, load, now);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Operational quality"
      source="DAT-04"
      description="What went wrong over time, and what people said about it."
      columns={["Period", "Cancelled", "SLA breaches", "Support cases", "Satisfaction"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose="No cancellation, breach, case or rating was recorded for this tenant in the selected period."
      filter={rangeFilter(range, setRange)}
      note={{
        kind: "info",
        glyph: "info",
        // The count matters as much as the mean, and the detail panel carries it for that reason.
        text: "Satisfaction is this period's mean, computed from the total and the count. Open a row to see how many ratings it rests on.",
      }}
      {...(loaded === undefined ? {} : { footnote: freshnessNote(loaded.freshestFactAt, now) })}
      exportName="analytics-quality"
      detailSubtitle="Operational quality · DAT-04"
      detailNote="A period with no ratings shows an em dash rather than zero, because no score is not a score of nothing."
    />
  );
}
